// Import Libraries
import React, { useState, useEffect } from "react";
import PageSEO from "../../../../Components/SEO/PageSEO";
import { getProducts } from "../../../../ApiLayer/apiLayer";

// Import Components
import ServerPageIntro from "../../../../Components/ServerPageIntro/ServerPageIntro";
import FilterComponent from "../../../../Components/FilterComponent/FilterComponent";
import ServerStandardsComponent from "../../../../Components/ServerStandardsComponent/ServerStandardsComponent";
import DedicatedServerLocations from "../StandardServersPage/Components/DedicatedServerLocations/DedicatedServerLocations";
import TestNetworkSpeedsComponent from "../StandardServersPage/Components/TestNetworkSpeedsComponent/TestNetworkSpeedsComponent";

// Import Assets
import GPU_Enabled_Server from "../../../../Assets/AI_Capable_Server.png";

// Import Animations
import FadeInOnScroll from "../../../../util/Animations/FadeInOnScroll";

const GpuEnabledServersPage = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await getProducts();
        if (response.products) {
          // Filter for GPU-enabled servers
          const gpuServers = response.products.filter(product => {
            const isGpuType = product.customdata?.type?.toLowerCase().includes('gpu');
            const hasGpu = product.customdata?.gpu && !product.customdata.gpu.toLowerCase().includes('none');
            return isGpuType || hasGpu;
          });
          setProducts(gpuServers);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchProducts();
  }, []);

  // Helper function to extract GPU models from products
  const getUniqueGpuModels = () => {
    const gpuModels = new Set();
    products.forEach(product => {
      if (product.customdata?.gpu && !product.customdata.gpu.toLowerCase().includes('none')) {
        gpuModels.add(product.customdata.gpu);
      }
    });
    return Array.from(gpuModels);
  };

  // Define structured data for the page
  const gpuServersSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "WebNX GPU-Enabled Dedicated Servers",
    "description": "High-performance GPU-enabled dedicated servers optimized for AI, deep learning, rendering, and scientific computing. Featuring NVIDIA RTX and Tesla GPUs with support for CUDA computing.",
    "brand": {
      "@type": "Brand",
      "name": "WebNX"
    },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "offers": products.map(product => ({
        "@type": "Offer",
        "name": `GPU Server: ${product.customdata?.gpu || 'GPU-Enabled'} with ${product.customdata?.processor || ''}`,
        "description": `${product.customdata?.gpu || ''} GPU Server with ${product.customdata?.processor || ''}, ${product.customdata?.memory || ''}, ${product.customdata?.storage || ''}, ${product.customdata?.network || ''}`,
        "price": product.pricing?.USD?.monthly || "",
        "priceCurrency": "USD",
        "availability": product.stockcontrol === "true" && product.stocklevel > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        "url": product.product_url || "",
        "itemCondition": "https://schema.org/NewCondition",
        "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        "seller": {
          "@type": "Organization",
          "name": "WebNX"
        }
      }))
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Server Type",
        "value": "GPU-Enabled Dedicated"
      },
      {
        "@type": "PropertyValue",
        "name": "Available GPU Models",
        "value": getUniqueGpuModels().join(", ")
      },
      {
        "@type": "PropertyValue",
        "name": "Use Cases",
        "value": "AI/ML, Deep Learning, Rendering, Scientific Computing"
      },
      {
        "@type": "PropertyValue",
        "name": "CUDA Support",
        "value": "Yes"
      },
      {
        "@type": "PropertyValue",
        "name": "Network",
        "value": "10Gbps with DDoS Protection"
      },
      {
        "@type": "PropertyValue",
        "name": "Support",
        "value": "24/7 Technical Support"
      }
    ],
    "applicationCategory": [
      "Machine Learning",
      "Artificial Intelligence",
      "3D Rendering",
      "Scientific Computing"
    ]
  };

  return (
    <div>
      <PageSEO
        title="GPU Enabled Servers | AI & Deep Learning Server Solutions"
        description="Power through demanding workloads with WebNX's GPU-enabled dedicated servers. Featuring NVIDIA RTX and Tesla GPUs with CUDA support, perfect for AI, deep learning, rendering, and scientific computing."
        keywords={`GPU enabled servers, GPU dedicated servers, AI servers, deep learning servers, scientific computing, WebNX GPU hosting, ${getUniqueGpuModels().join(', ')}, CUDA computing, machine learning servers`}
        path="/gpu-enabled-servers"
        image="https://webnx.com/assets/gpu-server.jpg"
        schema={gpuServersSchema}
        socialProfiles={{
          facebook: "https://www.facebook.com/WebNX/",
          linkedin: "https://www.linkedin.com/company/webnx/"
        }}
      />

      <FadeInOnScroll>
        <ServerPageIntro
          header="WebNX GPU-Enabled Servers"
          subheader="Accelerate Your Compute-Intensive Workloads"
          introText="Power through demanding workloads with our GPU-enabled dedicated servers. Featuring Intel Xeon processors and NVIDIA GPUs with up to 3,500 CUDA cores, these servers are specifically designed for AI, deep learning, and scientific computing. Whether you need the GTX 1080TI or a custom configuration, our team will build the perfect solution for your computational needs."
          image={GPU_Enabled_Server}
          hideImageOnMobile={true}
          features={[
            {
              icon: "FaMicrochip",
              title: "Latest GPUs",
              description: "NVIDIA RTX and Tesla GPUs for maximum computational power"
            },
            {
              icon: "FaBrain",
              title: "AI/ML Optimized",
              description: "Pre-configured for popular AI and ML frameworks with CUDA support"
            },
            {
              icon: "FaBolt",
              title: "High Performance",
              description: "Dual Intel Xeon processors and high-speed NVMe storage"
            },
            {
              icon: "FaChartLine",
              title: "Scalable Solutions",
              description: "Multi-GPU configurations available for enterprise workloads"
            }
          ]}
        />
      </FadeInOnScroll>

      {/* Filter Component */}
      <FilterComponent defaultFilters={{ "Server Type": ["GPU Enabled"] }} />

      <FadeInOnScroll>
        <ServerStandardsComponent />
      </FadeInOnScroll>

      <FadeInOnScroll>
        <DedicatedServerLocations />
      </FadeInOnScroll>

      <FadeInOnScroll>
        <TestNetworkSpeedsComponent />
      </FadeInOnScroll>
    </div>
  );
};

export default GpuEnabledServersPage;
